import React from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { CoverPhoto, CoverPhotoProps } from '@epoch-ui/components/CoverPhoto';

interface EditorHeaderProviderValue {
  isScrolled: boolean;
}

const EditorHeaderContext = React.createContext<EditorHeaderProviderValue>({
  isScrolled: false
});

// Approximate height difference between expanded and shrunk header
const HEADER_HEIGHT_BUFFER = 120;

export const EditorHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    const [isScrolled, setIsScrolled] = React.useState(false);

    const handleScroll = (): void => {
      setIsScrolled((prevIsScrolled) => {
        if (window.scrollY > 32 && !prevIsScrolled) {
          // Prevent feedback loop: don't shrink if it would make the page
          // fit in viewport (which would immediately trigger expansion)
          const currentScrollHeight = document.documentElement.scrollHeight;
          const viewportHeight = window.innerHeight;
          const shrunkScrollHeight = currentScrollHeight - HEADER_HEIGHT_BUFFER;

          if (shrunkScrollHeight <= viewportHeight) {
            return prevIsScrolled; // Don't shrink
          }
          return true;
        } else if (window.scrollY === 0 && prevIsScrolled) {
          return false;
        }
        return prevIsScrolled;
      });
    };

    React.useEffect((): (() => void) => {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
      <EditorHeaderContext.Provider value={{ isScrolled }}>
        <div
          ref={ref}
          {...props}
          data-is-scrolled={isScrolled}
          className={cn(
            'tw-sticky tw-top-0 tw-z-10 tw-min-h-32 tw-border-0 tw-border-b-2 tw-border-solid tw-border-border-grey tw-bg-gradient-to-b tw-from-light-blue-badge tw-to-white tw-px-4 tw-pt-9',
            className
          )}
        >
          {children}
        </div>
      </EditorHeaderContext.Provider>
    );
  }
);
EditorHeader.displayName = 'EditorHeader';

export const EditorHeaderContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return <div ref={ref} {...props} className={cn('tw-mx-auto tw-max-w-[900px]', className)} />;
});
EditorHeaderContainer.displayName = 'EditorHeaderContainer';

export const EditorHeaderTopNav = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={cn('tw-mb-4 tw-flex tw-items-start tw-justify-between', className)}
    />
  );
});
EditorHeaderTopNav.displayName = 'EditorHeaderTopNav';

export const EditorHeaderContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return <div ref={ref} {...props} className={cn('tw-mx-auto tw-flex tw-gap-x-4', className)} />;
});
EditorHeaderContent.displayName = 'EditorHeaderContent';

export const EditorHeaderCoverPhoto = React.forwardRef<HTMLImageElement, CoverPhotoProps>(
  ({ className, ...props }, ref) => {
    const { isScrolled } = React.useContext(EditorHeaderContext);

    return (
      <CoverPhoto
        ref={ref}
        data-is-scrolled={isScrolled}
        {...props}
        className={cn(
          'tw-h-auto tw-w-1/12 tw-transform tw-self-start tw-duration-500 tw-ease-in-out ',
          'data-[is-scrolled=true]:-tw-mr-4 data-[is-scrolled=false]:tw-w-[72px] ',
          'data-[is-scrolled=true]:tw-w-0 data-[is-scrolled=true]:tw-scale-0 ',
          'data-[is-scrolled=true]:tw-opacity-0',
          className
        )}
      />
    );
  }
);
EditorHeaderCoverPhoto.displayName = 'EditorHeaderCoverPhoto';

export const EditorHeaderInfo = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      {...props}
      className={cn('tw-flex tw-flex-1 tw-flex-col tw-justify-center', className)}
    />
  );
});
EditorHeaderInfo.displayName = 'EditorHeaderInfo';

export const EditorHeaderTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => {
  const { isScrolled } = React.useContext(EditorHeaderContext);
  return (
    <h1
      ref={ref}
      data-is-scrolled={isScrolled}
      {...props}
      className={cn(
        'tw-m-0 tw-text-[28px] tw-font-medium tw-leading-9 tw-transition-all tw-duration-500 tw-ease-in-out data-[is-scrolled=true]:tw-text-xl'
      )}
    >
      {children}
    </h1>
  );
});
EditorHeaderTitle.displayName = 'EditorHeaderTitle';

export const EditorHeaderCaption = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { isScrolled } = React.useContext(EditorHeaderContext);

  return (
    <div
      ref={ref}
      data-is-scrolled={isScrolled}
      {...props}
      className={cn(
        'tw-m-0 tw-transform tw-text-sm tw-leading-5 tw-text-primary-grey tw-duration-300 tw-ease-in-out data-[is-scrolled=true]:tw-m-0 data-[is-scrolled=true]:-tw-mt-5 data-[is-scrolled=true]:tw-opacity-0'
      )}
    />
  );
});
EditorHeaderCaption.displayName = 'EditorHeaderCaption';

export const EditorHeaderTabNav = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { isScrolled } = React.useContext(EditorHeaderContext);

  return (
    <div
      ref={ref}
      {...props}
      data-is-scrolled={isScrolled}
      className={cn(
        '-tw-mb-1 tw-mt-6 tw-transition-all tw-duration-500 tw-ease-in-out data-[is-scrolled=true]:tw-mt-2'
      )}
    />
  );
});
EditorHeaderTabNav.displayName = 'EditorHeaderTabNav';
